import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { JOURNEY_STEPS } from '../components/BuyerJourney';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { toast } from 'sonner';
import {
  CheckCircle, Circle, ChevronDown, ChevronUp, Lightbulb,
  ArrowRight, Home, Lock, ChevronRight
} from 'lucide-react';
import ProfessionalResourcesSection from '../components/ProfessionalResourcesSection';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StepIndicator = ({ steps, current, onSelect, user }) => (
  <div className="flex items-center justify-center gap-1 sm:gap-2 mb-10 flex-wrap" data-testid="buying-step-indicator">
    {steps.map((s, i) => (
      <React.Fragment key={s.id}>
        <button
          onClick={() => {
            if (i > 0 && !user) {
              toast.error('Please login to access this step');
              return;
            }
            onSelect(i);
          }}
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${
            i === current
              ? 'bg-[#E07A3A] text-white'
              : i < current
              ? 'bg-emerald-100 text-emerald-700'
              : i > 0 && !user
              ? 'bg-red-50 text-red-400 border border-red-200'
              : 'bg-slate-100 text-slate-400'
          }`}
          data-testid={`buying-step-nav-${i}`}
        >
          {i < current ? <CheckCircle className="h-3.5 w-3.5" /> : i > 0 && !user ? <Lock className="h-3.5 w-3.5" /> : <span>{i + 1}</span>}
          <span className="hidden sm:inline">{s.title.length > 18 ? s.title.slice(0, 18) + '...' : s.title}</span>
        </button>
        {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300 hidden sm:block" />}
      </React.Fragment>
    ))}
  </div>
);

const BuyingGuidePage = () => {
  const { user } = useAuth();
  const { country } = useCountry();
  const [currentStep, setCurrentStep] = useState(0);
  const [checkedItems, setCheckedItems] = useState({});
  const [showTip, setShowTip] = useState(null);
  const [loading, setLoading] = useState(true);

  const steps = JOURNEY_STEPS[country] || JOURNEY_STEPS.UK;
  const step = steps[currentStep];

  useEffect(() => {
    const loadProgress = async () => {
      if (!user) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API_URL}/api/buyer-journey/progress`);
        if (res.data?.checked_items) setCheckedItems(res.data.checked_items);
      } catch (err) { logger.error('Load progress:', err); }
      setLoading(false);
    };
    loadProgress();
  }, [user]);

  const saveProgress = async (updated) => {
    if (!user) return;
    try {
      await axios.post(`${API_URL}/api/buyer-journey/progress`, { checked_items: updated, country });
    } catch (err) { logger.error('Save progress:', err); }
  };

  const toggleItem = (itemId) => {
    if (!user) {
      toast.error('Please sign in to track your progress');
      return;
    }
    const updated = { ...checkedItems, [itemId]: !checkedItems[itemId] };
    setCheckedItems(updated);
    saveProgress(updated);
  };

  const getStepProgress = (s) => {
    const checked = s.items.filter(i => checkedItems[i.id]).length;
    return { checked, total: s.items.length, percent: Math.round((checked / s.items.length) * 100) };
  };

  const totalChecked = steps.reduce((acc, s) => acc + s.items.filter(i => checkedItems[i.id]).length, 0);
  const totalItems = steps.reduce((acc, s) => acc + s.items.length, 0);
  const overallPercent = Math.round((totalChecked / totalItems) * 100);

  const countryLabel = country === 'IN' ? 'India' : country === 'CA' ? 'Canada' : 'United Kingdom';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="buying-guide-page">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-8">
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-2 block uppercase font-semibold">
            Buying Journey &mdash; {countryLabel}
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#0F172A] font-semibold">
            Your Path to<span className="italic text-[#E07A3A]"> Home Ownership</span>
          </h1>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm">
            A step-by-step guide covering everything from instructing a solicitor to collecting your keys. Tick off each item as you go.
          </p>
        </div>

        {/* Pulse Rating beginner explainer — surfaces the £4.99 product
            in the buyer's own learning context. */}
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 p-6 mb-8" data-testid="buying-guide-pulse-explainer">
          <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-medium">Before you start browsing</span>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mt-1.5">What is a Pulse Rating?</h2>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            Every property on Shopperden gets a <strong>Pulse Rating from 0&nbsp;to&nbsp;100</strong> &mdash;
            think of it as a <strong>credit score for the home</strong>. Our AI re-checks five things
            every Monday morning: price growth, rental yield, how fast the local market moves,
            legal compliance (RERA in India / EPC in the UK) and whether new infrastructure is being built nearby.
          </p>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Green &middot; 70-100</div>
              <div className="text-xs text-slate-700 mt-0.5">Strong buy</div>
            </div>
            <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Amber &middot; 40-69</div>
              <div className="text-xs text-slate-700 mt-0.5">Look closer</div>
            </div>
            <div className="rounded-lg border-l-4 border-red-500 bg-red-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Red &middot; 0-39</div>
              <div className="text-xs text-slate-700 mt-0.5">Walk away</div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Without it, you&rsquo;re guessing. One bad purchase costs &pound;20,000+ in stamp duty,
            legal fees and a lost deposit. Pulse Premium subscribers get the <strong>Top 10
            Green-rated</strong> matches emailed every Monday for &pound;4.99 / &#8377;499 / C$8.99 a month.
          </p>
          <Link to="/pulse" className="inline-block mt-4 px-4 py-2 bg-[#E07A3A] text-white text-xs uppercase tracking-widest rounded hover:bg-[#C4652E] font-semibold" data-testid="buying-guide-pulse-link">
            Read the full Pulse explainer &rarr;
          </Link>
        </div>

        {/* Overall Progress */}
        {user && (
          <div className="max-w-md mx-auto mb-8" data-testid="buying-overall-progress">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>{totalChecked} of {totalItems} tasks</span>
              <span className="font-semibold text-[#E07A3A]">{overallPercent}%</span>
            </div>
            <div className="h-2 bg-slate-100 overflow-hidden">
              <div className="h-full bg-[#E07A3A] transition-all duration-500" style={{ width: `${overallPercent}%` }} />
            </div>
          </div>
        )}

        {!user && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-50 border border-red-200 text-center" data-testid="buying-login-prompt">
            <Lock className="h-5 w-5 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600 font-semibold">Please login to access full guide and track progress</p>
            <p className="text-xs text-red-400 mt-1">Step 1 is free to preview. Sign in to unlock all steps.</p>
            <Link to="/register">
              <Button className="mt-3 bg-red-500 text-white hover:bg-red-600 text-xs tracking-widest uppercase font-semibold px-6 py-4" data-testid="buying-guide-register-btn">
                Sign Up Free <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        )}

        {/* Step Indicator */}
        <StepIndicator steps={steps} current={currentStep} onSelect={setCurrentStep} user={user} />

        {/* Current Step Content */}
        {step && (
          <div className="max-w-3xl mx-auto" data-testid={`buying-step-content-${currentStep}`}>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#0F172A] text-white flex items-center justify-center mx-auto mb-4">
                {step.icon || <Home className="h-7 w-7" />}
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] font-semibold mb-2">{step.title}</h2>
              <p className="text-slate-500 text-sm max-w-lg mx-auto">{step.description}</p>
              {user && (() => {
                const p = getStepProgress(step);
                return (
                  <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 bg-slate-50 text-xs text-slate-500 font-medium">
                    <span>{p.checked}/{p.total} complete</span>
                    <div className="w-20 h-1.5 bg-slate-200">
                      <div className={`h-full transition-all ${p.percent === 100 ? 'bg-emerald-500' : 'bg-[#E07A3A]'}`} style={{ width: `${p.percent}%` }} />
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Checklist Items */}
            <div className="space-y-3 mb-10">
              {step.items.map((item) => (
                <div key={item.id} className="border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`buying-item-${item.id}`}>
                  <div className="flex items-start gap-3 p-4">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="flex-shrink-0 mt-0.5"
                      data-testid={`buying-check-${item.id}`}
                    >
                      {checkedItems[item.id] ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <Circle className="h-5 w-5 text-slate-300 hover:text-[#E07A3A] transition-colors" />
                      )}
                    </button>
                    <p className={`flex-1 text-sm leading-relaxed ${checkedItems[item.id] ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                      {item.label}
                    </p>
                    <button
                      onClick={() => setShowTip(showTip === item.id ? null : item.id)}
                      className={`flex-shrink-0 p-1 transition-colors ${showTip === item.id ? 'text-[#E07A3A]' : 'text-slate-300 hover:text-[#E07A3A]'}`}
                      data-testid={`buying-tip-${item.id}`}
                    >
                      <Lightbulb className="h-4 w-4" />
                    </button>
                  </div>
                  {showTip === item.id && (
                    <div className="mx-4 mb-4 p-3 bg-amber-50 border border-amber-100 text-sm text-amber-800 leading-relaxed" data-testid={`buying-tip-content-${item.id}`}>
                      <span className="font-semibold text-[#E07A3A] text-xs uppercase tracking-wider block mb-1">Shopperden Guidance</span>
                      {item.tip}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-6 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="text-xs tracking-widest uppercase font-semibold border-slate-200"
                data-testid="buying-prev-btn"
              >
                Previous
              </Button>
              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={() => {
                    if (currentStep >= 0 && !user) {
                      toast.error('Please login to access the next step');
                      return;
                    }
                    setCurrentStep(currentStep + 1);
                  }}
                  className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-widest uppercase font-semibold px-8 py-5"
                  data-testid="buying-next-btn"
                >
                  {!user && currentStep === 0 ? (
                    <><Lock className="mr-2 h-4 w-4" />Login to Continue</>
                  ) : (
                    <>Next Step <ArrowRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              ) : (
                <Link to="/mortgage-guide">
                  <Button
                    className="bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs tracking-widest uppercase font-semibold px-8 py-5"
                    data-testid="buying-mortgage-link-btn"
                  >
                    Mortgage Guide <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Professional directory deep-links */}
        <div className="mt-10">
          <ProfessionalResourcesSection context="buying" country={country || 'UK'} />
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-[#0F172A] p-8 text-center" data-testid="buying-guide-cta">
          <Home className="h-10 w-10 text-[#E07A3A] mx-auto mb-4" />
          <h3 className="font-['Playfair_Display'] text-2xl text-white font-semibold mb-2">Ready to Find Your Home?</h3>
          <p className="text-white/50 text-sm max-w-lg mx-auto mb-6">
            Browse verified properties with 360° tours, AI valuations, and direct seller connections.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/properties">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-8 py-5 text-sm tracking-widest uppercase font-semibold" data-testid="buying-browse-btn">
                Browse Properties <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/mortgage-guide">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-5 text-sm tracking-widest uppercase font-semibold" data-testid="buying-mortgage-btn">
                Mortgage Guide
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyingGuidePage;
